import {
  DESIGN_HEIGHT,
  DESIGN_WIDTH,
  cornerPinMatrix,
  dilateQuad,
  planeDensity,
  scaleQuad,
} from './geometry';
import type { Vec2 } from './geometry';
import type { SceneRegion } from './scenes/types';

export interface PlaneBox {
  /** The plane's own box in local px (always at least 1 × 1). */
  width: number;
  height: number;
  /** Local pixels per metre — a texture tile of `m` metres is `m × density` px wide. */
  density: number;
  /** `matrix3d(…)` that pins the plane onto the region's quad at stage scale `k`. */
  matrix: string;
  /** `polygon(…)` in percentages of the frame, only for regions that carry an outline. */
  clipPath: string | undefined;
}

/**
 * How one stone surface is laid out at stage scale `k`: the plane's local size (dense enough that the
 * stone is never magnified), the corner-pin matrix, and an optional outline. Pure — memoise by
 * `(region, k)`.
 */
export function planeBox(region: SceneRegion, k: number): PlaneBox {
  const quad = scaleQuad(dilateQuad(region.quad, region.bleed ?? 0.8), k);
  const density = planeDensity(region.quad, region.size) * k;
  const width = Math.max(1, Math.round(region.size[0] * density));
  const height = Math.max(1, Math.round(region.size[1] * density));
  return {
    width,
    height,
    density,
    matrix: cornerPinMatrix(quad, width, height),
    clipPath: region.mask ? outlinePolygon(region.mask) : undefined,
  };
}

/** `polygon(10% 20%, …)`: an outline in design px expressed in percentages, so it needs no scale factor. */
export function outlinePolygon(points: readonly Vec2[]): string {
  const pct = (value: number, of: number): string =>
    `${String(Math.round((value / of) * 10000) / 100)}%`;
  return `polygon(${points
    .map(([x, y]) => `${pct(x, DESIGN_WIDTH)} ${pct(y, DESIGN_HEIGHT)}`)
    .join(',')})`;
}
